// =============================================================================
// FILE: src/providers/AuthProvider.jsx
// MODULE: Auth — Session Provider
// PROJECT: Arcus Sports Management
//
// KEY FIX: Uses supabase.auth.getUser() to get the userId, then makes
// the profile query with the Authorization header set explicitly.
// This bypasses the navigator lock that blocks regular .from() queries
// when called immediately after an auth event.
// =============================================================================

import { createContext, useContext, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/store/useAppStore'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const { setUser, setProfile, setTenant, setSessionChecked, clearSession } = useAppStore()

  useEffect(() => {
    let ignore = false

    // -------------------------------------------------------------------------
    // Listen for auth state changes — this is the primary mechanism.
    // onAuthStateChange gives us the session directly, so we use the
    // access_token from the session to make authenticated DB queries
    // WITHOUT going through the auth lock.
    // -------------------------------------------------------------------------
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (ignore) return
        console.log('[AuthProvider] event:', event)

        if (event === 'SIGNED_OUT' || !session) {
          clearSession()
          setSessionChecked(true)
          return
        }

        // Only (re)load the profile on events that actually change WHO is logged
        // in. TOKEN_REFRESHED and USER_UPDATED fire on a routine cadence (every
        // ~hour, or sooner) and do NOT change the user/role/tenant — reloading
        // the profile on them is wasteful and, worse, exposes a transient fetch
        // failure that used to bounce the user out via window.location. We keep
        // the session marked as checked on those events but skip the reload.
        const profileLoadEvents = ['INITIAL_SESSION', 'SIGNED_IN']
        if (session?.user && profileLoadEvents.includes(event)) {
          try {
            setUser(session.user)
            // Pass the access token directly — bypasses the navigator lock
            await loadUserProfile(session.user.id, session.access_token, { setProfile, setTenant })
          } catch (err) {
            console.error('[AuthProvider] profile load error:', err)
          } finally {
            if (!ignore) setSessionChecked(true)
          }
        } else if (session?.user) {
          // TOKEN_REFRESHED / USER_UPDATED: keep the user object fresh (new
          // access token) but do not reload the profile or touch navigation.
          setUser(session.user)
          if (!ignore) setSessionChecked(true)
        }
      }
    )

    return () => {
      ignore = true
      subscription.unsubscribe()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return <AuthContext.Provider value={{}}>{children}</AuthContext.Provider>
}

// ---------------------------------------------------------------------------
// loadUserProfile
// Uses the access_token directly in the Authorization header instead of
// relying on the Supabase client's internal auth state (which holds the lock).
// ---------------------------------------------------------------------------
async function loadUserProfile(userId, accessToken, { setProfile, setTenant }) {
  console.log('[AuthProvider] Loading profile for:', userId)

  const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/users?id=eq.${userId}&select=id,role,full_name,email,avatar_url,team_id,jersey_number,position,onboarded,tenant_id,created_at,tenants(id,name,slug,subscription_tier,subscription_status,active_tournament_count,logo_url,primary_color,addons)`

  const response = await fetch(url, {
    headers: {
      'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type':  'application/json',
    },
  })

  if (!response.ok) {
    console.warn('[AuthProvider] Profile fetch failed:', response.status, await response.text())
    return
  }

  const rows = await response.json()
  const profile = rows[0]

  if (!profile) {
    // No profile row yet (e.g. brand-new signup before onboarding writes it).
    // DO NOT redirect with window.location here — a native redirect forces a
    // full page reload, remounts the whole app, and on any transient empty
    // response would eject the user mid-task (this was the volleyball-registration
    // "app resets itself" bug). Routing to onboarding is ProtectedRoute's job
    // (it uses <Navigate>, an internal transition with no reload). Here we just
    // leave the profile unset and return.
    console.warn('[AuthProvider] No profile found for user:', userId)
    return
  }

  console.log('[AuthProvider] Profile loaded:', profile?.role, profile?.email)
  setProfile(profile)
  if (profile?.tenants) setTenant(profile.tenants)
}

// ---------------------------------------------------------------------------
// useAuth hook
// ---------------------------------------------------------------------------
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === null) throw new Error('[Arcus] useAuth() must be used inside <AuthProvider>.')

  const { user, role, profile, tenant, sessionChecked, clearSession } = useAppStore()

  const signOut = async () => {
    clearSession()
    await supabase.auth.signOut()
  }

  return {
    user, role, profile, tenant, sessionChecked,
    isAuthenticated: !!user,
    isLoading:       !sessionChecked,
    isOnboarded:     profile?.onboarded ?? false,
    signOut,
  }
}
